import type React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Eye, EyeOff, GripVertical, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IClaimFormSectionConfig } from '@/@types/claims';
import type { EditorAction } from './editorReducer';
import { FieldEditor } from './FieldEditor';

// LOCAL_TYPE_JUSTIFIED: editor-internal props — tightly scoped to this feature, not reused elsewhere
interface ISectionListProps {
  sections: IClaimFormSectionConfig[];
  dispatch: React.Dispatch<EditorAction>;
}

// LOCAL_TYPE_JUSTIFIED: editor-internal props — tightly scoped to this feature, not reused elsewhere
interface ISortableSectionProps {
  section: IClaimFormSectionConfig;
  dispatch: React.Dispatch<EditorAction>;
}

function SortableSection({ section, dispatch }: ISortableSectionProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionKey,
  });

  const fieldSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleFieldDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = section.fields.findIndex(f => f.fieldKey === active.id);
    const newIndex = section.fields.findIndex(f => f.fieldKey === over.id);
    const newOrder = arrayMove(
      section.fields.map(f => f.fieldKey),
      oldIndex,
      newIndex
    );
    dispatch({ type: 'REORDER_FIELDS', sectionKey: section.sectionKey, fieldKeys: newOrder });
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'transition-shadow',
        isDragging && 'shadow-lg opacity-80 bg-background z-50 rounded-lg'
      )}
    >
      <AccordionItem value={section.sectionKey} className="border rounded-lg px-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing touch-none p-0.5 rounded text-muted-foreground hover:text-foreground mr-1"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder section"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <AccordionTrigger className="flex-1 py-3 hover:no-underline">
            <span className={cn('font-medium text-sm', !section.visible && 'line-through text-muted-foreground')}>
              {section.label}
            </span>
          </AccordionTrigger>
          <div className="flex items-center gap-1 ml-2">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={e => { e.stopPropagation(); dispatch({ type: 'TOGGLE_SECTION_VISIBLE', sectionKey: section.sectionKey }); }}
            >
              {section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={e => { e.stopPropagation(); dispatch({ type: 'REMOVE_SECTION', sectionKey: section.sectionKey }); }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <AccordionContent>
          <div className="pb-2">
            <div className="mb-2">
              <Label className="text-xs text-muted-foreground">Section Label</Label>
              <Input
                value={section.label}
                onChange={e => dispatch({ type: 'UPDATE_SECTION_LABEL', sectionKey: section.sectionKey, label: e.target.value })}
                className="h-8 text-sm mt-1"
              />
            </div>
            <Separator className="my-2" />
            <div className="flex items-center gap-3 px-1 py-1 text-xs font-medium text-muted-foreground border-b mb-1">
              <span className="w-6 shrink-0" />
              <span className="flex-1">Field Label</span>
              <span className="w-20 text-center shrink-0">Visible</span>
              <span className="w-20 text-center shrink-0">Required</span>
            </div>
            {section.fields.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">No fields in this section yet.</p>
            )}
            <DndContext
              sensors={fieldSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleFieldDragEnd}
            >
              <SortableContext
                items={section.fields.map(f => f.fieldKey)}
                strategy={verticalListSortingStrategy}
              >
                {section.fields.map(field => (
                  <FieldEditor
                    key={field.fieldKey}
                    field={field}
                    sectionKey={section.sectionKey}
                    dispatch={dispatch}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 text-xs"
              onClick={() => dispatch({
                type: 'ADD_FIELD',
                sectionKey: section.sectionKey,
                field: {
                  fieldKey: `field_${Date.now()}`,
                  label: 'New Field',
                  visible: true,
                  required: false,
                  sortOrder: section.fields.length,
                },
              })}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Field
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
}

export function SectionList({ sections, dispatch }: ISectionListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleSectionDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.sectionKey === active.id);
    const newIndex = sections.findIndex(s => s.sectionKey === over.id);
    const newOrder = arrayMove(sections.map(s => s.sectionKey), oldIndex, newIndex);
    dispatch({ type: 'REORDER_SECTIONS', sectionKeys: newOrder });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleSectionDragEnd}
    >
      <SortableContext items={sections.map(s => s.sectionKey)} strategy={verticalListSortingStrategy}>
        <Accordion type="multiple" defaultValue={sections.map(s => s.sectionKey)} className="space-y-2">
          {sections.map(section => (
            <SortableSection key={section.sectionKey} section={section} dispatch={dispatch} />
          ))}
        </Accordion>
      </SortableContext>
    </DndContext>
  );
}
